import React, { useState, useEffect } from "react";
import DatePicker from "react-datepicker";
import NavBar from "./NavBar";
import "./HeroSection.css";
import "react-datepicker/dist/react-datepicker.css";

function HeroSection({ onVideoLoaded }) {
  const [checkIn, setCheckIn] = useState(null);
  const [checkOut, setCheckOut] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // Check if device is mobile
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    // Video loading fallback
    const fallbackTimer = setTimeout(() => {
      console.log("Video loading timeout - showing page anyway");
      if (onVideoLoaded) {
        onVideoLoaded();
      }
    }, 5000);

    return () => {
      clearTimeout(fallbackTimer);
      window.removeEventListener("resize", checkMobile);
    };
  }, [onVideoLoaded]);

  const formatDateForMakeMyTrip = (date) => {
    if (!date) return "";
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    return `${month}${day}${year}`;
  };

  const getTomorrowDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow;
  };

  const getMinCheckOutDate = () => {
    if (!checkIn) return getTomorrowDate();

    const checkInDate = new Date(checkIn);
    checkInDate.setDate(checkInDate.getDate() + 1);
    return checkInDate;
  };

  const handleCheckInChange = (date) => {
    setCheckIn(date);

    if (checkOut && date && checkOut <= date) {
      setCheckOut(null);
    }
  };

  const handleSearch = () => {
    console.log("Search clicked:", { checkIn, checkOut });

    if (checkIn && checkOut) {
      const formattedCheckIn = formatDateForMakeMyTrip(checkIn);
      const formattedCheckOut = formatDateForMakeMyTrip(checkOut);

      const makeMyTripUrl = `https://www.makemytrip.com/hotels/hotel-details/?hotelId=202502280018169176&city=CTNOI&country=IN&roomStayQualifier=2e0e1e0e&checkin=${formattedCheckIn}&checkout=${formattedCheckOut}&openDetail=true&altAcco=true&locusId=CTNOI&locusType=city&region=in&rsc=2e3e&mpn=false`;

      window.open(makeMyTripUrl, "_blank");
    } else {
      alert("Please select both check-in and check-out dates");
    }
  };

  const handleVideoLoaded = () => {
    console.log("Video loaded successfully");
    if (onVideoLoaded) {
      onVideoLoaded();
    }
  };

  const handleVideoCanPlay = () => {
    console.log("Video can play");
    handleVideoLoaded();
  };

  const handleVideoLoadedData = () => {
    console.log("Video data loaded");
    handleVideoLoaded();
  };

  const handleVideoLoadedMetadata = () => {
    console.log("Video metadata loaded");
    handleVideoLoaded();
  };

  const handleVideoError = (e) => {
    console.log("Video loading error:", e);
    handleVideoLoaded();
  };

  const CalendarIcon = () => (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M8 2V5M16 2V5M3.5 9.09H20.5M21 8.5V17C21 20 19.5 22 16 22H8C4.5 22 3 20 3 17V8.5C3 5.5 4.5 3.5 8 3.5H16C19.5 3.5 21 8.5 21 8.5Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M11.9955 13.7H12.0045M8.29449 13.7H8.30349M8.29449 17.7H8.30349"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  const SearchIcon = () => (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2" />
      <path
        d="m21 21-4.35-4.35"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  // Common DatePicker props
  const datePickerProps = {
    dateFormat: "dd/MM/yyyy",
    className: "searchInput dateInput",
    autoComplete: "off",
    showPopperArrow: false,
    showMonthDropdown: true,
    showYearDropdown: true,
    dropdownMode: "select",
    // Mobile-specific props
    ...(isMobile && {
      withPortal: true,
      portalId: "datepicker-portal",
    }),
  };

  return (
    <div className="heroContainer">
      <NavBar />

      <video
        className="heroVideo"
        autoPlay
        muted
        loop
        playsInline
        onCanPlay={handleVideoCanPlay}
        onCanPlayThrough={handleVideoCanPlay}
        onLoadedData={handleVideoLoadedData}
        onLoadedMetadata={handleVideoLoadedMetadata}
        onError={handleVideoError}
        preload="auto"
      >
        <source
          src="https://nimantrana-media.s3.ap-south-1.amazonaws.com/bg.mp4"
          type="video/mp4"
        />
        Your browser does not support the video tag.
      </video>

      <div className="heroOverlay"></div>

      {/* Portal container for mobile datepicker */}
      <div id="datepicker-portal"></div>

      <div className="heroContent">
        <div className="heroTextContainer">
          <h1 className="heroMainText">
            Where every stay
            <br />
            become <span style={{ color: "#DFB25A" }}>an experience.</span>
          </h1>
        </div>

        <div className="heroSearchContainer">
          <div className="searchForm">
            <div className="searchInputGroup">
              <div className="inputWrapper checkInWrapper">
                <CalendarIcon />
                <div className="inputContent">
                  <label className="inputLabel">Check-in</label>
                  <DatePicker
                    selected={checkIn}
                    onChange={handleCheckInChange}
                    minDate={getTomorrowDate()}
                    placeholderText="dd/mm/yyyy"
                    {...datePickerProps}
                  />
                </div>
              </div>

              <div className="inputDivider"></div>

              <div className="inputWrapper checkOutWrapper">
                <CalendarIcon />
                <div className="inputContent">
                  <label className="inputLabel">Check-out</label>
                  <DatePicker
                    selected={checkOut}
                    onChange={setCheckOut}
                    minDate={getMinCheckOutDate()}
                    placeholderText="dd/mm/yyyy"
                    {...datePickerProps}
                  />
                </div>
              </div>

              <button className="heroSearchButton" onClick={handleSearch}>
                <SearchIcon />
                Search
              </button>
            </div>
          </div>
        </div>

        <div className="heroStatsContainer">
          <div className="statItem">
            <div className="statNumber">300+</div>
            <div style={{ color: "#DFB25A" }} className="statLabel">
              Reviews
            </div>
          </div>
          <div className="statItem">
            <div className="statNumber">10,000+</div>
            <div style={{ color: "#DFB25A" }} className="statLabel">
              Happy Customer
            </div>
          </div>
          <div className="statItem">
            <div className="statNumber">100+</div>
            <div style={{ color: "#DFB25A" }} className="statLabel">
              Experienced Guide
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default HeroSection;
